import { reportHandledException } from './error-reporting/report'

/**
 * Site-wide guard against page translators (Chrome's built-in Google Translate
 * and similar extensions). They replace text nodes with `<font>` elements, but
 * React keeps the original nodes; when React later removes such a node, or
 * inserts before one, the DOM throws NotFoundError, and the nearest error
 * boundary unmounts the whole screen. React will not fix this
 * (facebook/react#11538); this is the workaround posted in that thread.
 *
 * Only the two "wrong parent" cases are intercepted:
 * - `removeChild(child)` where `child.parentNode !== this`: the translator moved
 *   `child` (under a `<font>`, or elsewhere while reordering a sentence), so it is
 *   removed from the parent it has now; when the translator replaced it, it is
 *   already detached and nothing happens. Returns `child`;
 * - `insertBefore(newNode, referenceNode)` where `referenceNode` is a node whose
 *   `parentNode !== this`: the translator moved the reference node, so `newNode`
 *   is appended as the last child of `this`. Returns `newNode`.
 * Every other call, including every other error, goes to the original method.
 * The new node may land after text that sits elsewhere on screen, but the screen
 * (and anything it was doing, such as an upload) stays alive and up to date.
 *
 * The first interception per page load is reported to Sentry with the tag
 * `guard: translation_dom_guard`.
 */

const GUARD_TAG = 'translation_dom_guard'
const SOURCE = 'lib/translation-dom-guard.ts'

/**
 * Kept on `globalThis` under a registry symbol rather than in module scope, so a
 * re-evaluated copy of this module (HMR) finds the guard already installed and
 * does not wrap the wrapper.
 */
const STATE_KEY = Symbol.for('kiao-voice.translation-dom-guard')

interface GuardState {
  originalRemoveChild: Node['removeChild']
  originalInsertBefore: Node['insertBefore']
  reported: boolean
}

type GuardGlobal = typeof globalThis & { [STATE_KEY]?: GuardState }

function isNode(value: unknown): value is Node {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { nodeType?: unknown }).nodeType === 'number'
  )
}

const ACTIONS = {
  removedMoved: 'removed the child from the parent it was moved to',
  alreadyDetached: 'found the child already detached',
  appended: 'appended the new node as the last child',
} as const

function reportOnce(
  state: GuardState,
  operation: 'removeChild' | 'insertBefore',
  action: keyof typeof ACTIONS
): void {
  if (state.reported) return
  state.reported = true
  reportHandledException(
    new Error(`Page translation moved a node React manages; ${operation} ${ACTIONS[action]}`),
    { source: SOURCE, guard: GUARD_TAG, operation }
  )
}

/**
 * Installs the guard on `Node.prototype`. Idempotent; does nothing where there
 * is no DOM (`Node` undefined, e.g. server rendering).
 */
export function installTranslationDomGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return
  const global = globalThis as GuardGlobal
  if (global[STATE_KEY]) return

  const state: GuardState = {
    originalRemoveChild: Node.prototype.removeChild,
    originalInsertBefore: Node.prototype.insertBefore,
    reported: false,
  }
  global[STATE_KEY] = state

  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (isNode(child) && child.parentNode !== this) {
      if (child.parentNode) {
        reportOnce(state, 'removeChild', 'removedMoved')
        state.originalRemoveChild.call(child.parentNode, child)
      } else {
        reportOnce(state, 'removeChild', 'alreadyDetached')
      }
      return child
    }
    return state.originalRemoveChild.call(this, child) as T
  }

  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (isNode(referenceNode) && referenceNode.parentNode !== this) {
      reportOnce(state, 'insertBefore', 'appended')
      return state.originalInsertBefore.call(this, newNode, null) as T
    }
    return state.originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}

/**
 * Test-only: restores the original `Node.prototype` methods and forgets that a
 * report was sent, so one test's patch never leaks into the next.
 */
export function uninstallTranslationDomGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return
  const global = globalThis as GuardGlobal
  const state = global[STATE_KEY]
  if (!state) return
  Node.prototype.removeChild = state.originalRemoveChild
  Node.prototype.insertBefore = state.originalInsertBefore
  delete global[STATE_KEY]
}
